// Parse the per-language audio zips and the numbered images zip.
// Audio zip layout (founder's audio-dashboard export):
//   "English Audio.zip" -> NN-slug_en.mp3 ... + manifest.json
// Images zip layout: 1.jpg / 2.jpg / ... (also accepts 01.jpg, .jpeg, .png)
//
// ISOMORPHIC + LAZY: works in Node and the browser. Zip entries are only
// LISTED up front (name + size via fflate's filter callback, no inflation);
// file bytes are extracted on demand through each file's getData() thunk.
// This keeps browser memory at ~one file at a time instead of ~1 GB for five
// fully-unpacked audio zips. Inputs are {name, data: Uint8Array}.

import { unzipSync } from "fflate";
import { TAB_LANG } from "./constants";
import { blocker, warning, info } from "./issues";

const AUDIO_ENTRY = /^0*(\d{1,3})-(.*)_([a-z]{2})\.mp3$/i;
const IMAGE_ENTRY = /^0*(\d{1,3})\.(jpe?g|png)$/i;

function baseName(p) {
  const parts = p.split("/");
  return parts[parts.length - 1];
}

// List entries WITHOUT inflating: the filter callback sees every entry's
// metadata; returning false skips decompression entirely.
function listEntries(u8) {
  const meta = [];
  unzipSync(u8, {
    filter: (f) => {
      meta.push({ path: f.name, size: f.originalSize ?? f.size ?? 0 });
      return false;
    },
  });
  return meta;
}

function extractEntry(u8, entryPath) {
  const out = unzipSync(u8, { filter: (f) => f.name === entryPath });
  return out[entryPath];
}

export function looksLikeMp3(data) {
  if (!data || data.length < 3) return false;
  if (data[0] === 0x49 && data[1] === 0x44 && data[2] === 0x33) return true; // ID3
  return data[0] === 0xff && (data[1] & 0xe0) === 0xe0; // MPEG frame sync
}

export function imageKind(data) {
  if (!data || data.length < 8) return null;
  if (data[0] === 0xff && data[1] === 0xd8 && data[2] === 0xff) return "jpg";
  if (data[0] === 0x89 && data[1] === 0x50 && data[2] === 0x4e && data[3] === 0x47) return "png";
  return null;
}

function ignorable(entryPath) {
  const base = baseName(entryPath);
  return entryPath.endsWith("/") || base.startsWith(".") || entryPath.includes("__MACOSX");
}

/** Parse one per-language audio zip. source: {name, data: Uint8Array|Buffer} */
export function parseAudioZip(source, issues) {
  const name = source.name;
  const u8 = source.data instanceof Uint8Array ? source.data : new Uint8Array(source.data);
  const entries = listEntries(u8);

  // language from the zip filename ("French Audio.zip")
  let lang = null;
  const m = name.match(/^(.+?)\s*audio\.zip$/i);
  if (m) lang = TAB_LANG[m[1].trim().toLowerCase()] || null;

  // manifest: prefer the shallowest manifest.json, warn if several
  let manifest = null;
  const manifestPaths = entries.filter((e) => !ignorable(e.path) && baseName(e.path) === "manifest.json");
  if (manifestPaths.length > 1) issues.push(warning("multiple-manifests", `[${name}] ${manifestPaths.length} manifest.json files found — using the top-level one`));
  if (manifestPaths.length) {
    const best = manifestPaths.sort((a, b) => a.path.split("/").length - b.path.split("/").length)[0];
    try {
      const raw = extractEntry(u8, best.path);
      manifest = JSON.parse(new TextDecoder().decode(raw));
    } catch {
      issues.push(warning("bad-manifest", `[${name}] manifest.json is not valid JSON — duration checks skipped`));
    }
  }

  const files = new Map(); // sl -> {entryName, path, size, slug, lang, getData}
  const strays = [];
  for (const e of entries) {
    if (ignorable(e.path)) continue;
    const base = baseName(e.path);
    if (base === "manifest.json") continue;
    const em = base.match(AUDIO_ENTRY);
    if (!em) {
      strays.push(base);
      continue;
    }
    const sl = parseInt(em[1], 10);
    const fileLang = em[3].toLowerCase();
    if (!lang) lang = fileLang; // fall back to the suffix
    if (fileLang !== lang) {
      issues.push(blocker("lang-suffix-mismatch", `[${name}] "${base}" has language suffix "${fileLang}" but the zip is ${lang}`, { sl, lang }));
      continue;
    }
    if (files.has(sl)) {
      issues.push(blocker("dup-audio", `[${name}] two files for stop ${sl}`, { sl, lang }));
      continue;
    }
    files.set(sl, {
      entryName: base,
      path: e.path,
      size: e.size,
      slug: em[2].toLowerCase(),
      lang: fileLang,
      getData: () => extractEntry(u8, e.path),
    });
  }

  if (!lang) issues.push(blocker("zip-lang-unknown", `[${name}] could not determine the language of this zip (name it "<Language> Audio.zip")`));
  if (strays.length) issues.push(warning("stray-files", `[${name}] ${strays.length} file(s) ignored (unexpected names): ${strays.slice(0, 5).join(", ")}${strays.length > 5 ? "…" : ""}`));

  return { name, lang, files, manifest };
}

/** Parse the numbered stop-images zip. source: {name, data: Uint8Array|Buffer} */
export function parseImagesZip(source, issues) {
  const name = source.name;
  const u8 = source.data instanceof Uint8Array ? source.data : new Uint8Array(source.data);
  const files = new Map(); // sl -> {entryName, path, size, getData}
  const strays = [];
  for (const e of listEntries(u8)) {
    if (ignorable(e.path)) continue;
    const base = baseName(e.path);
    const em = base.match(IMAGE_ENTRY);
    if (!em) {
      strays.push(base);
      continue;
    }
    const sl = parseInt(em[1], 10);
    if (files.has(sl)) {
      issues.push(blocker("dup-image", `[${name}] two images for stop ${sl}`, { sl }));
      continue;
    }
    files.set(sl, { entryName: base, path: e.path, size: e.size, getData: () => extractEntry(u8, e.path) });
  }
  if (strays.length) issues.push(warning("stray-files", `[${name}] ${strays.length} file(s) ignored (name images 1.jpg, 2.jpg, …): ${strays.slice(0, 5).join(", ")}${strays.length > 5 ? "…" : ""}`));
  return { name, files };
}


